import { NgClass } from '@angular/common';
import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ScreenshotService } from '@core/services/screenshot.service';

@Component({
  imports: [NgClass],
  selector: 'app-icon',
  templateUrl: './icon.component.html',
})
export class IconComponent {
  private readonly screenshotService = inject(ScreenshotService);
  private readonly sanitizer = inject(DomSanitizer);
  private static readonly svgCache = new Map<string, string>();

  public readonly src = input.required<string>();
  public readonly size = input<number | null>(null);
  public readonly alt = input<string>('icon');
  public readonly class = input<string>('w-6 h-6');
  public readonly color = input<string>('var(--shikenso-purple-base)');
  public readonly noMaskSize = input<boolean>(false);
  public readonly ignoreColor = input<boolean>(false);
  public readonly backgroundColor = input<string | null>(null);

  public readonly resolvedSrc = computed((): string => {
    const rawSrc = this.src();

    if (/^(blob:|data:|http:|https:)/.test(rawSrc)) {
      return rawSrc;
    }

    if (rawSrc.startsWith('/')) {
      return new URL(rawSrc.slice(1), document.baseURI).toString();
    }

    return rawSrc;
  });

  public readonly svgContent = signal<SafeHtml | null>(null);
  public readonly maskUrl = computed((): string => `url(${this.resolvedSrc()})`);
  public readonly maskSize = computed(() => (this.noMaskSize() ? 'contain' : `${this.size()}px`));

  constructor() {
    // Only load SVG when screenshot is being captured
    effect(() => {
      const isCapturing = this.screenshotService.isCapturing();
      const ignoreColor = this.ignoreColor();
      const src = this.resolvedSrc();

      if (!ignoreColor && src && isCapturing) {
        void this.loadSvg(src);
      } else {
        this.svgContent.set(null);
      }
    });
  }

  private async loadSvg(url: string): Promise<void> {
    try {
      // Check cache first (cache by URL only, not color)
      let svgContent = IconComponent.svgCache.get(url);

      if (!svgContent) {
        const response = await fetch(url);

        if (!response.ok) {
          return;
        }

        svgContent = await response.text();
        // Cache the original SVG
        IconComponent.svgCache.set(url, svgContent);
      }

      const computedColor = this.getComputedColor();

      // Apply color to cached SVG
      const modifiedSvg = this.applyColorToSvg(svgContent, computedColor);

      this.svgContent.set(this.sanitizer.bypassSecurityTrustHtml(modifiedSvg));
    } catch {
      // If loading fails, fall back to mask-image approach
    }
  }

  private applyColorToSvg(svgContent: string, computedColor: string): string {
    let modified = svgContent;

    // Replace currentColor in both fill and stroke
    modified = modified.replace(/fill="currentColor"/gi, `fill="${computedColor}"`);
    modified = modified.replace(/stroke="currentColor"/gi, `stroke="${computedColor}"`);

    // Replace color values in fill (but preserve fill="none")
    modified = modified.replace(/fill="([^"]*)"/g, (match, fillValue) => {
      if (fillValue === 'none') {
        return match;
      }

      if (/^#|^rgb|^rgba/.test(fillValue)) {
        return `fill="${computedColor}"`;
      }

      return match;
    });

    // Replace color values in stroke (but preserve stroke="none")
    modified = modified.replace(/stroke="([^"]*)"/g, (match, strokeValue) => {
      if (strokeValue === 'none') {
        return match;
      }

      if (/^#|^rgb|^rgba/.test(strokeValue)) {
        return `stroke="${computedColor}"`;
      }

      return match;
    });

    return modified;
  }

  private getComputedColor(): string {
    const colorValue = this.color();

    if (!colorValue.startsWith('var(')) {
      return this.rgbToHex(colorValue);
    }

    // Get CSS variable value
    const cssVarName = colorValue.match(/var\(([^)]+)\)/)?.[1]?.trim();

    if (cssVarName) {
      const computedValue = window.getComputedStyle(document.documentElement).getPropertyValue(cssVarName).trim();

      if (computedValue) {
        return this.rgbToHex(computedValue);
      }
    }

    // Fallback: compute from temporary element
    const tempDiv = document.createElement('div');
    tempDiv.style.backgroundColor = colorValue;
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    document.body.appendChild(tempDiv);
    const computedColor = window.getComputedStyle(tempDiv).backgroundColor;
    document.body.removeChild(tempDiv);

    return this.rgbToHex(computedColor) || '#1f0224';
  }

  private rgbToHex(color: string): string {
    const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);

    if (match) {
      const r = parseInt(match[1], 10);
      const g = parseInt(match[2], 10);
      const b = parseInt(match[3], 10);

      return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
    }

    return color;
  }
}
